/**
 * NotificationPermissionPrompt — Landlord
 * Non-blocking prompt shown to logged-in mobile users when notification
 * permission has not yet been requested (state === 'default') and the
 * prompt hasn't been dismissed before.
 *
 * Rendered inside authenticated routes in App.tsx.
 *
 * Checks Notification.permission directly rather than the Firebase-dependent
 * hook state, so the prompt works in test environments without Firebase.
 */

import { useState } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';

const DISMISSED_KEY = 'pwa_notif_prompt_dismissed';

export function NotificationPermissionPrompt() {
  // AuthProvider's instance owns foreground-message display for the app session.
  const { requestPermissionAndRegister } = usePushNotifications({ handleForegroundMessages: false });
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === 'true'
  );

  // Check browser Notification API directly — not through Firebase-dependent hook state
  const browserPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  // Installed desktop PWA (standalone window) gets the same proactive prompt as
  // mobile — a plain desktop browser tab does not, to avoid over-prompting casual
  // visitors who haven't opted into the app-like experience.
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (navigator as any).standalone === true;

  if (browserPermission !== 'default' || dismissed || !(isMobile || isStandalone)) return null;

  const handleAccept = async () => {
    await requestPermissionAndRegister();
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, 'true');
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, 'true');
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Enable push notifications"
      data-testid="notification-permission-prompt"
      className="fixed bottom-16 left-4 right-4 z-50 bg-card border border-border rounded-lg shadow-xl p-4 flex flex-col gap-3"
    >
      <p className="text-sm text-foreground m-0">
        Get notified when you receive new messages, even when the app is closed.
      </p>
      <div className="flex gap-2">
        <button
          data-testid="notif-prompt-enable-btn"
          onClick={handleAccept}
          className="flex-1 px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:opacity-90"
        >
          Enable notifications
        </button>
        <button
          data-testid="notif-prompt-dismiss-btn"
          onClick={handleDismiss}
          className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
